import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  // Action creators
  showToast,
  dismissToast,
  clearAllToasts,
  showModal,
  closeModal,
  closeAllModals,
  showAlert,
  dismissAlert,
  clearAllAlerts,
  showConfirmDialog,
  dismissConfirmDialog,
  toggleMainMenu,
  toggleNotifications,
  toggleSearch,
  toggleSidebar,
  setActiveTab,
  setHeaderVisibility,
  setFooterVisibility,
  setNavbarVisibility,
  setContentWidth,
  setContentPadding,
  setScrollPosition,
  trackClick,
  trackHover,
  trackFocus,
  setInitialLoadComplete,
  setGlobalLoading,
  setPageLoading,
  registerCallback,
  unregisterCallback,
  clearCallbackRegistry,
  resetUIState,
  
  // Selectors
  selectToasts,
  selectModals,
  selectAlerts,
  selectActiveConfirmDialog,
  selectMenu,
  selectLayout,
  selectInteraction,
  selectIsGlobalLoading,
  selectPageLoadingStatus,
  selectIsMainMenuOpen,
  selectIsNotificationsOpen,
  selectIsSearchOpen,
  selectIsSidebarOpen,
  selectActiveTab,
  selectIsAnyMenuOpen,
  selectIsHeaderVisible,
  selectIsFooterVisible,
  selectIsNavbarVisible,
  selectScrollPosition,
  selectContentWidth,
  selectContentPadding,
  selectLastInteractionTime,
  selectIsUserInteracting,
  selectIsInitialLoadComplete,
  selectIsAnyModalOpen,
  selectHasActiveConfirmDialog
} from '../store/slices/uiSlice';

import {
  ToastType,
  ModalSize,
  AlertType,
  ConfirmDialogType} from '../types/ui';

// Callback registry type for tracking registered callbacks
type CallbackRegistry = {
  [key: string]: (...args: any[]) => void;
};

// Create a callback registry that lives outside Redux
// This allows storing actual functions which can't be serialized in Redux
const callbackRegistry: CallbackRegistry = {};

/**
 * Custom hook for managing application UI state
 */
export const useAppUI = () => {
  const dispatch = useAppDispatch();
  
  // Select state from Redux
  const toasts = useAppSelector(selectToasts);
  const modals = useAppSelector(selectModals);
  const alerts = useAppSelector(selectAlerts);
  const activeConfirmDialog = useAppSelector(selectActiveConfirmDialog);
  const menu = useAppSelector(selectMenu);
  const layout = useAppSelector(selectLayout);
  const interaction = useAppSelector(selectInteraction);
  const isGlobalLoading = useAppSelector(selectIsGlobalLoading);
  const pageLoadingStatus = useAppSelector(selectPageLoadingStatus);
  
  // Specific UI state selectors
  const isMainMenuOpen = useAppSelector(selectIsMainMenuOpen);
  const isNotificationsOpen = useAppSelector(selectIsNotificationsOpen);
  const isSearchOpen = useAppSelector(selectIsSearchOpen);
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  const activeTab = useAppSelector(selectActiveTab);
  const isAnyMenuOpen = useAppSelector(selectIsAnyMenuOpen);
  const isHeaderVisible = useAppSelector(selectIsHeaderVisible);
  const isFooterVisible = useAppSelector(selectIsFooterVisible);
  const isNavbarVisible = useAppSelector(selectIsNavbarVisible);
  const scrollPosition = useAppSelector(selectScrollPosition);
  const contentWidth = useAppSelector(selectContentWidth);
  const contentPadding = useAppSelector(selectContentPadding);
  const lastInteractionTime = useAppSelector(selectLastInteractionTime);
  const isUserInteracting = useAppSelector(selectIsUserInteracting);
  const isInitialLoadComplete = useAppSelector(selectIsInitialLoadComplete);
  const isAnyModalOpen = useAppSelector(selectIsAnyModalOpen);
  const hasActiveConfirmDialog = useAppSelector(selectHasActiveConfirmDialog);
  
  // Toast methods
  const addToast = useCallback((params: {
    type: ToastType;
    message: string;
    title?: string;
    autoClose?: boolean;
    duration?: number;
    actionLabel?: string;
    actionCallback?: (...args: any[]) => void;
  }) => {
    const { actionCallback, ...toastParams } = params;
    
    // If there's a callback, register it
    let callbackId: string | undefined;
    if (actionCallback) {
      callbackId = `toast_action_${Date.now()}_${Math.random()}`;
      callbackRegistry[callbackId] = actionCallback;
      
      // Register in Redux for visibility (not the actual function)
      dispatch(registerCallback({ 
        id: callbackId, 
        description: `Toast action callback: ${params.message.substring(0, 30)}...` 
      }));
    }
    
    dispatch(showToast({
      ...toastParams,
      actionCallback: callbackId
    }));
  }, [dispatch]);
  
  const removeToast = useCallback((id: string) => {
    dispatch(dismissToast(id));
  }, [dispatch]);
  
  const removeAllToasts = useCallback(() => {
    dispatch(clearAllToasts());
  }, [dispatch]);
  
  // Execute a toast action callback
  const executeToastAction = useCallback((callbackId: string) => {
    if (callbackRegistry[callbackId]) {
      callbackRegistry[callbackId]();
    }
  }, []);
  
  // Modal methods
  const openModal = useCallback((params: {
    component: string;
    props?: Record<string, any>;
    title?: string;
    size?: ModalSize;
    closable?: boolean;
    fullscreen?: boolean;
    hasBackdrop?: boolean;
    backdropClickClose?: boolean;
    onClose?: () => void;
  }) => {
    const { onClose, ...modalParams } = params;
    
    // If there's a close callback, register it
    let callbackId: string | undefined;
    if (onClose) {
      callbackId = `modal_close_${Date.now()}_${Math.random()}`;
      callbackRegistry[callbackId] = onClose;
      
      // Register in Redux for visibility (not the actual function)
      dispatch(registerCallback({ 
        id: callbackId, 
        description: `Modal close callback: ${params.component}`
      }));
    }
    
    dispatch(showModal({
      ...modalParams,
      onClose: callbackId
    }));
  }, [dispatch]);
  
  const closeModalById = useCallback((id: string) => {
    dispatch(closeModal(id));
  }, [dispatch]);
  
  const closeAllModalsAction = useCallback(() => {
    dispatch(closeAllModals());
  }, [dispatch]);
  
  // Alert methods
  const addAlert = useCallback((params: {
    type: AlertType;
    message: string;
    title?: string;
    dismissable?: boolean;
    expiresAt?: number;
  }) => {
    dispatch(showAlert(params));
  }, [dispatch]);
  
  const removeAlert = useCallback((id: string) => {
    dispatch(dismissAlert(id));
  }, [dispatch]);
  
  const removeAllAlerts = useCallback(() => {
    dispatch(clearAllAlerts());
  }, [dispatch]);
  
  // Confirm dialog methods
  const confirmDialog = useCallback((params: {
    type: ConfirmDialogType;
    message: string;
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void;
    onCancel?: () => void;
    destructive?: boolean;
  }) => {
    const { onConfirm, onCancel, ...dialogParams } = params;
    
    // Register confirm callback
    const confirmCallbackId = `confirm_${Date.now()}_${Math.random()}`;
    callbackRegistry[confirmCallbackId] = onConfirm;
    
    // Register in Redux for visibility (not the actual function)
    dispatch(registerCallback({ 
      id: confirmCallbackId, 
      description: `Confirm dialog confirm callback: ${params.message.substring(0, 30)}...`
    }));
    
    // Register cancel callback if provided
    let cancelCallbackId: string | undefined;
    if (onCancel) {
      cancelCallbackId = `cancel_${Date.now()}_${Math.random()}`;
      callbackRegistry[cancelCallbackId] = onCancel;
      
      // Register in Redux for visibility (not the actual function)
      dispatch(registerCallback({ 
        id: cancelCallbackId, 
        description: `Confirm dialog cancel callback: ${params.message.substring(0, 30)}...`
      }));
    }
    
    dispatch(showConfirmDialog({
      ...dialogParams,
      confirmCallback: confirmCallbackId,
      cancelCallback: cancelCallbackId
    }));
  }, [dispatch]);
  
  const cancelConfirmDialog = useCallback(() => {
    // Execute cancel callback if exists
    if (activeConfirmDialog?.cancelCallback) {
      if (callbackRegistry[activeConfirmDialog.cancelCallback]) {
        callbackRegistry[activeConfirmDialog.cancelCallback]();
      }
      
      // Unregister the callback
      dispatch(unregisterCallback(activeConfirmDialog.cancelCallback));
      delete callbackRegistry[activeConfirmDialog.cancelCallback];
    }
    
    // Unregister confirm callback too
    if (activeConfirmDialog?.confirmCallback) {
      dispatch(unregisterCallback(activeConfirmDialog.confirmCallback));
      delete callbackRegistry[activeConfirmDialog.confirmCallback];
    }
    
    // Dismiss the dialog
    dispatch(dismissConfirmDialog());
  }, [dispatch, activeConfirmDialog]);
  
  const confirmAction = useCallback(() => {
    // Execute confirm callback
    if (activeConfirmDialog?.confirmCallback) {
      if (callbackRegistry[activeConfirmDialog.confirmCallback]) {
        callbackRegistry[activeConfirmDialog.confirmCallback]();
      }
      
      // Unregister the callback
      dispatch(unregisterCallback(activeConfirmDialog.confirmCallback));
      delete callbackRegistry[activeConfirmDialog.confirmCallback];
    }
    
    // Unregister cancel callback too if exists
    if (activeConfirmDialog?.cancelCallback) {
      dispatch(unregisterCallback(activeConfirmDialog.cancelCallback));
      delete callbackRegistry[activeConfirmDialog.cancelCallback];
    }
    
    // Dismiss the dialog
    dispatch(dismissConfirmDialog());
  }, [dispatch, activeConfirmDialog]);
  
  // Menu controls
  const toggleMainMenuState = useCallback((open?: boolean) => {
    dispatch(toggleMainMenu(open));
  }, [dispatch]);
  
  const toggleNotificationsState = useCallback((open?: boolean) => {
    dispatch(toggleNotifications(open));
  }, [dispatch]);
  
  const toggleSearchState = useCallback((open?: boolean) => {
    dispatch(toggleSearch(open));
  }, [dispatch]);
  
  const toggleSidebarState = useCallback((open?: boolean) => {
    dispatch(toggleSidebar(open));
  }, [dispatch]);
  
  const changeActiveTab = useCallback((tab: string) => {
    dispatch(setActiveTab(tab));
  }, [dispatch]);
  
  // Layout controls
  const showHeader = useCallback((visible: boolean) => {
    dispatch(setHeaderVisibility(visible));
  }, [dispatch]);
  
  const showFooter = useCallback((visible: boolean) => {
    dispatch(setFooterVisibility(visible));
  }, [dispatch]);
  
  const showNavbar = useCallback((visible: boolean) => {
    dispatch(setNavbarVisibility(visible));
  }, [dispatch]);
  
  const changeContentWidth = useCallback((width: 'full' | 'constrained') => {
    dispatch(setContentWidth(width));
  }, [dispatch]);
  
  const changeContentPadding = useCallback((padding: 'none' | 'small' | 'medium' | 'large') => {
    dispatch(setContentPadding(padding));
  }, [dispatch]);
  
  const updateScrollPosition = useCallback((position: number) => {
    dispatch(setScrollPosition(position));
  }, [dispatch]);
  
  // Interaction tracking
  const trackElementClick = useCallback((elementId: string | null) => {
    dispatch(trackClick(elementId));
  }, [dispatch]);
  
  const trackElementHover = useCallback((elementId: string | null) => {
    dispatch(trackHover(elementId));
  }, [dispatch]);
  
  const trackElementFocus = useCallback((elementId: string | null) => {
    dispatch(trackFocus(elementId));
  }, [dispatch]);
  
  const setInitialLoadCompleted = useCallback((completed: boolean) => {
    dispatch(setInitialLoadComplete(completed));
  }, [dispatch]);
  
  // Loading states
  const setGlobalLoadingState = useCallback((loading: boolean) => {
    dispatch(setGlobalLoading(loading));
  }, [dispatch]);
  
  const setPageLoadingState = useCallback((page: string, loading: boolean) => {
    dispatch(setPageLoading({ page, isLoading: loading }));
  }, [dispatch]);
  
  const isPageLoading = useCallback((page: string) => {
    return !!pageLoadingStatus[page];
  }, [pageLoadingStatus]);
  
  // Reset UI state
  const resetUI = useCallback(() => {
    // Clear callback registry
    dispatch(clearCallbackRegistry());
    Object.keys(callbackRegistry).forEach(key => {
      delete callbackRegistry[key];
    });
    
    dispatch(resetUIState());
  }, [dispatch]);
  
  return {
    // State
    toasts,
    modals,
    alerts,
    activeConfirmDialog,
    menu,
    layout,
    interaction,
    isGlobalLoading,
    pageLoadingStatus,
    
    // UI Status selectors
    isMainMenuOpen,
    isNotificationsOpen,
    isSearchOpen,
    isSidebarOpen,
    activeTab,
    isAnyMenuOpen,
    isHeaderVisible,
    isFooterVisible,
    isNavbarVisible,
    scrollPosition,
    contentWidth,
    contentPadding,
    lastInteractionTime,
    isUserInteracting,
    isInitialLoadComplete,
    isAnyModalOpen,
    hasActiveConfirmDialog,
    
    // Toast methods
    addToast,
    removeToast,
    removeAllToasts,
    executeToastAction,
    
    // Modal methods
    openModal,
    closeModalById,
    closeAllModals: closeAllModalsAction,
    
    // Alert methods
    addAlert,
    removeAlert,
    removeAllAlerts,
    
    // Confirm dialog methods
    confirmDialog,
    cancelConfirmDialog,
    confirmAction,
    
    // Menu controls
    toggleMainMenu: toggleMainMenuState,
    toggleNotifications: toggleNotificationsState,
    toggleSearch: toggleSearchState,
    toggleSidebar: toggleSidebarState,
    changeActiveTab,
    
    // Layout controls
    showHeader,
    showFooter,
    showNavbar,
    changeContentWidth,
    changeContentPadding,
    updateScrollPosition,
    
    // Interaction tracking
    trackElementClick,
    trackElementHover,
    trackElementFocus,
    setInitialLoadCompleted,
    
    // Loading states
    setGlobalLoadingState,
    setPageLoadingState,
    isPageLoading,
    
    // Reset UI state
    resetUI
  };
};

export default useAppUI;